import React, { useState } from 'react'
import styled from 'styled-components'
import { observer } from 'mobx-react'
import { DashboardPositionStore } from 'stores/dashboard/dashboard-position'
import {
  DashboardPositionFormAlertText,
  DashboardPositionFormContentCard,
  DashboardPositionFormSection,
  DashboardPositionFormSectionTitle,
} from 'containers/dashboard/positions/styles'
import { Column, Row } from 'components/common/layout'
import { PositionFormSubInfoTitleText } from 'components/dashboard/positions/styles'
import { ChipSize, ChipWrapper, RadioChip } from 'components/common/chip'
import { OptionItem, Select, SelectSize } from 'components/common/select'
import { BoxInput } from 'components/common/input'
import { Location, StartupPositionRemoteWorkEnum } from 'types/common'
import { useBaseInfo, useDashboardStartupDetail } from 'apis/hooks'
import { STARTUP_POSITION_REMOTE_WORK_TYPE } from 'infra/constants'

export const DashboardPositionFormLocation: React.FC<{
  store: DashboardPositionStore
}> = observer(({ store }) => {
  const { data: baseInfo } = useBaseInfo()
  const { data: startup } = useDashboardStartupDetail()
  const [selectedParentLocation, setSelectedParentLocation] =
    useState<Location | null>(null)

  const locations =
    (selectedParentLocation &&
      baseInfo?.locations.filter(
        (item) => selectedParentLocation.id === item.parentId && !item.isRemote,
      )) ||
    []

  return (
    <DashboardPositionFormSection>
      <DashboardPositionFormSectionTitle>
        근무지
      </DashboardPositionFormSectionTitle>
      <DashboardPositionFormContentCard>
        <Column style={{ gap: 8 }}>
          <Column style={{ gap: 4 }}>
            <PositionFormSubInfoTitleText>
              근무지가 회사 주소와 같나요?
            </PositionFormSubInfoTitleText>
            <PositionFormSubInfoTitleText>
              (회사 주소: {startup?.address})
            </PositionFormSubInfoTitleText>
          </Column>
          <ChipWrapper>
            <RadioChip
              size={ChipSize.SMALL}
              isSelected={!!store.position.isSameLocation}
              onClick={() => {
                store.setIsSameLocation(true)
                store.setLocation(null)
                store.setAddress('')
                store.onDataChanged()
              }}
            >
              네
            </RadioChip>
            <RadioChip
              size={ChipSize.SMALL}
              isSelected={store.position.isSameLocation === false}
              onClick={() => {
                store.setIsSameLocation(false)
                store.onDataChanged()
              }}
            >
              아니요
            </RadioChip>
          </ChipWrapper>
        </Column>
        {store.position.isSameLocation === false && (
          <Column style={{ gap: 8 }}>
            <PositionFormSubInfoTitleText>
              근무지의 주소를 알려주세요
            </PositionFormSubInfoTitleText>
            <LocationDetail>
              <Row style={{ gap: 8 }}>
                <Select
                  width={120}
                  size={SelectSize.SMALL}
                  value={selectedParentLocation?.name || null}
                  placeholder='위치 분류'
                >
                  {baseInfo?.locations
                    .filter((item) => !item.parentId && !item.isRemote)
                    .map((item) => (
                      <OptionItem
                        key={item.id}
                        isSelected={item.id === selectedParentLocation?.id}
                        onClick={() => setSelectedParentLocation(item)}
                      >
                        {item.name}
                      </OptionItem>
                    ))}
                </Select>
                {locations.length > 0 && (
                  <Select
                    width={120}
                    size={SelectSize.SMALL}
                    value={store.position.location?.name || null}
                    placeholder='지역 분류'
                  >
                    {locations.map((item) => (
                      <OptionItem
                        key={item.id}
                        isSelected={item.id === store.position.location?.id}
                        onClick={() => {
                          store.setLocation(item)
                          store.onDataChanged()
                        }}
                      >
                        {item.name}
                      </OptionItem>
                    ))}
                  </Select>
                )}
              </Row>
              <BoxInput
                value={store.position.address}
                placeholder='전체 도로명 주소'
                style={{ padding: '6px 12px', flex: 1 }}
                onChange={(e) => {
                  store.setAddress(e.target.value)
                  store.onDataChanged()
                }}
              />
            </LocationDetail>
            {(!store.position.publishedAt ||
              store.position.address.length === 0) && (
              <DashboardPositionFormAlertText>
                * 전체 도로명 주소를 정확히 입력하지 않으면 지도에 잘못 표시돼요
              </DashboardPositionFormAlertText>
            )}
          </Column>
        )}
        <Column style={{ gap: 8 }}>
          <PositionFormSubInfoTitleText>
            원격 근무가 가능한가요?
          </PositionFormSubInfoTitleText>
          <Column style={{ gap: 8 }}>
            {STARTUP_POSITION_REMOTE_WORK_TYPE.map((item) => (
              <RadioChip
                size={ChipSize.SMALL}
                isSelected={store.position.remoteWorkPreference === item}
                key={item}
                onClick={() => {
                  store.setRemoteWorkPreference(item)
                  store.onDataChanged()
                }}
              >
                {StartupPositionRemoteWorkEnum[item]}
              </RadioChip>
            ))}
          </Column>
        </Column>
      </DashboardPositionFormContentCard>
    </DashboardPositionFormSection>
  )
})

const LocationDetail = styled(Row)`
  gap: 8px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    align-items: start;
    flex-direction: column;
  }
`
